import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { purchasingService } from "../services/dataService";
import { PurchaseOrderDetail, PurchaseOrderListItem, PurchaseOrderStatus as S, PurchasingStats, Supplier } from "../types";
import { formatCurrency, turkishIncludes } from "../utils/formatters";
import { usePermissions } from "../context/PermissionContext";
import { PO_STATUS_LABEL, poDue, poErr, poFmtDate, poFmtQty, poIsOpen, poIsWaiting } from "../utils/purchasing";
import PageLoader from "../components/PageLoader";
import AlertModal from "../components/AlertModal";
import ConfirmModal from "../components/ConfirmModal";
import SupplierModal from "../components/SupplierModal";
import ReceiveModal from "../components/ReceiveModal";
import PurchaseCancelModal from "../components/PurchaseCancelModal";
import PurchaseOrderDrawer from "../components/PurchaseOrderDrawer";
import PurchaseOrderModal from "../components/PurchaseOrderModal";
import { SupplierPickerField } from "../components/SupplierPicker";
import { firmHue, firmInitials } from "../components/FirmPicker";
import "./PurchaseOrders.css";

export { PO_STATUS_LABEL };

/**
 * Satın Alma — sipariş defteri + tedarikçiler. Tek rota (/purchasing/*):
 *   /purchasing           liste
 *   /purchasing/<id>      liste + sipariş kartı (çekmece) — Tedarikçiler ekranı ve bildirim bağlantıları buraya gelir
 *   /purchasing/new       yeni sipariş modalı açılır, adres /purchasing'e döner (?supplier=<id> ön seçim)
 * Yeni sipariş ve taslak düzenleme sayfa değil modaldir (PurchaseOrderModal). Tüm işlemler ve onaylar bu sayfadadır.
 */

type Tab = "orders" | "suppliers";
type Chip = "open" | "draft" | "ordered" | "partial" | "received" | "cancelled" | "all";
const CHIPS: [Chip, string][] = [["open", "Açık"], ["draft", "Taslak"], ["ordered", "İletildi"], ["partial", "Kısmen alındı"], ["received", "Tamamlanan"], ["cancelled", "İptal"], ["all", "Hepsi"]];
const chipMatch = (c: Chip, s: S) =>
  c === "all" ? true : c === "open" ? poIsOpen(s) : c === "draft" ? s === S.Draft : c === "ordered" ? s === S.Ordered
    : c === "partial" ? s === S.PartiallyReceived : c === "received" ? s === S.Received : s === S.Cancelled;

const I = {
  search: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>,
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>,
  refresh: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12a8 8 0 1 1-2.3-5.6" /><path d="M20 4v5h-5" /></svg>,
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
  edit: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" /></svg>,
  eye: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></svg>,
  filterOff: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 5h16l-6 7v6l-4 2v-8z" /><path d="m3 3 18 18" /></svg>,
  inbox: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v16H4z" /><path d="M4 13h5l2 3h2l2-3h5" /></svg>,
  send: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2 11 13M22 2l-7 20-4-9-9-4z" /></svg>,
  receive: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12M7 10l5 5 5-5M4 19h16" /></svg>,
  cart: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="20" r="1.4" /><circle cx="18" cy="20" r="1.4" /><path d="M2 3h3l2.6 12h11.2L21 7H6" /></svg>,
  arrow: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>,
  warn: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3 2 20h20z" /><path d="M12 10v4M12 17.5v.5" /></svg>,
};

/** Ana tutar TRY (varsa), diğer para birimleri alt satırda. */
const moneyMain = (m?: Record<string, number>) => {
  const entries = Object.entries(m || {}).filter(([, v]) => Math.abs(v) > 0.004);
  const main = entries.find(([c]) => c === "TRY") ?? entries[0] ?? null;
  const rest = entries.filter((e) => e !== main).map(([c, v]) => formatCurrency(v, c)).join(" · ");
  return { main, rest };
};

const Purchasing: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const seg = (useParams()["*"] || "").split("/")[0]; // "" | "new" | <id>
  const drawerId = seg && seg !== "new" ? seg : null;
  const { has, hasModule, hasScreen, loaded: permsLoaded } = usePermissions();
  const canOrders = has("purchasing:orders:view");
  const canSuppliers = has("purchasing:suppliers:view");
  const canUpdate = has("purchasing:orders:update");

  // Sekme türetilir (yetkiler sayfa açıldıktan sonra gelir): yalnız tedarikçi yetkisi olan rol sipariş uçlarına giremez (403), Tedarikçiler sekmesinde kalır
  const [tabPick, setTab] = useState<Tab>(searchParams.get("tab") === "suppliers" ? "suppliers" : "orders");
  const tab: Tab = !canOrders ? "suppliers" : tabPick === "suppliers" && !canSuppliers ? "orders" : tabPick;
  const [stats, setStats] = useState<PurchasingStats | null>(null);
  const [orders, setOrders] = useState<PurchaseOrderListItem[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]); // pasifler dahil
  const [initial, setInitial] = useState(true);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [loaded, setLoaded] = useState(false); // en az bir kez başarıyla yüklendi
  const [q, setQ] = useState("");
  const [chip, setChip] = useState<Chip>("open");
  const [lateOnly, setLateOnly] = useState(false);
  const [supplierFilter, setSupplierFilter] = useState("");
  const [sq, setSq] = useState("");
  const [sChip, setSChip] = useState<"active" | "passive" | "all">("active");
  const [modal, setModal] = useState<{ open: boolean; order: PurchaseOrderDetail | null; supplierId?: string }>({ open: false, order: null });
  const [refreshKey, setRefreshKey] = useState(0);
  const [receiveFor, setReceiveFor] = useState<PurchaseOrderDetail | null>(null);
  const [cancelFor, setCancelFor] = useState<PurchaseOrderListItem | null>(null);
  const [cancelErr, setCancelErr] = useState("");
  const [supplierModal, setSupplierModal] = useState<{ open: boolean; supplier?: Supplier | null }>({ open: false });
  const [rowBusy, setRowBusy] = useState<string | null>(null);
  const [alert, setAlert] = useState<{ isOpen: boolean; message: string; type: "info" | "success" | "warning" | "error" }>({ isOpen: false, message: "", type: "info" });
  const [confirm, setConfirm] = useState<{ isOpen: boolean; title: string; message: string; type: "info" | "warning" | "danger"; confirmText: string; onConfirm: () => void }>({ isOpen: false, title: "", message: "", type: "info", confirmText: "Onayla", onConfirm: () => {} });
  const ok = (message: string) => setAlert({ isOpen: true, message, type: "success" });
  const fail = (message: string) => setAlert({ isOpen: true, message, type: "error" });
  const warn = (message: string) => setAlert({ isOpen: true, message, type: "warning" });
  const closeConfirm = () => setConfirm((c) => ({ ...c, isOpen: false }));

  const load = useCallback(async () => {
    setLoadError("");
    try {
      const [s, o, sup] = await Promise.all([
        canOrders ? purchasingService.stats() : Promise.resolve(null),
        canOrders ? purchasingService.orders() : Promise.resolve([] as PurchaseOrderListItem[]),
        purchasingService.suppliers(true),
      ]);
      setStats(s); setOrders(o); setSuppliers(sup); setLoaded(true);
    } catch (e: any) { setLoadError(poErr(e, "Satın alma verileri yüklenemedi.")); }
    finally { setInitial(false); }
  }, [canOrders]);
  // Yetkiler gelmeden yüklenmez: sipariş uçları ve /new yetki kontrolü yetki listesine bağlı
  useEffect(() => { if (permsLoaded) load(); }, [load, permsLoaded]);

  const refresh = async () => { setBusy(true); await load(); setRefreshKey((k) => k + 1); setBusy(false); };
  const reloadSuppliers = async () => { const s = await purchasingService.suppliers(true); setSuppliers(s); return s; };

  // /purchasing/new → modal; adres listeye döner (yenilemede modal tekrar açılmaz)
  useEffect(() => {
    if (seg !== "new" || initial) return;
    if (has("purchasing:orders:create")) setModal({ open: true, order: null, supplierId: searchParams.get("supplier") || undefined });
    else warn("Sipariş oluşturma yetkiniz yok.");
    navigate("/purchasing", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seg, initial]);

  const openDrawer = (id: string) => navigate(`/purchasing/${id}`, { state: { fromList: true } });
  const closeDrawer = () => ((location.state as any)?.fromList ? navigate(-1) : navigate("/purchasing", { replace: true }));

  // ---- Türetilmiş ----
  const counts = useMemo(() => Object.fromEntries(CHIPS.map(([k]) => [k, orders.filter((o) => chipMatch(k, o.status)).length])) as Record<Chip, number>, [orders]);
  const lateCount = useMemo(() => orders.filter((o) => o.isOverdue).length, [orders]);
  const rows = useMemo(() => {
    const terms = q.trim().split(/\s+/).filter(Boolean);
    const rank = (o: PurchaseOrderListItem) => (o.isOverdue ? 0 : poIsWaiting(o.status) ? (o.expectedDate ? 1 : 2) : o.status === S.Draft ? 3 : 4);
    return orders.filter((o) => {
      if (!chipMatch(chip, o.status)) return false;
      if (lateOnly && !o.isOverdue) return false;
      if (supplierFilter && o.supplierId !== supplierFilter) return false;
      const hay = [o.number, o.supplierName, o.createdBy || "", String(o.grossTotal)];
      return terms.every((t) => hay.some((h) => turkishIncludes(h, t)));
    }).sort((a, b) => {
      const ra = rank(a), rb = rank(b);
      if (ra !== rb) return ra - rb;
      if (ra <= 1) return (a.expectedDate || "") < (b.expectedDate || "") ? -1 : 1; // en eski/en yakın teslim önce
      return a.orderDate < b.orderDate ? 1 : -1;
    });
  }, [orders, chip, lateOnly, supplierFilter, q]);
  const attention = useMemo(() => orders
    .map((o) => ({ o, d: poDue(o.expectedDate, o.status) }))
    .filter((x) => x.d.state !== "")
    .sort((a, b) => (a.d.days ?? 0) - (b.d.days ?? 0))
    .slice(0, 6), [orders]);
  const listTotals = useMemo(() => {
    const m = new Map<string, number>();
    rows.forEach((o) => { if (o.status !== S.Cancelled) m.set(o.currency, (m.get(o.currency) ?? 0) + o.grossTotal); });
    return Array.from(m.entries());
  }, [rows]);
  const openBySupplier = useMemo(() => {
    const m = new Map<string, { count: number; late: number }>();
    orders.forEach((o) => { if (!poIsOpen(o.status)) return; const x = m.get(o.supplierId) ?? { count: 0, late: 0 }; x.count++; if (o.isOverdue) x.late++; m.set(o.supplierId, x); });
    return m;
  }, [orders]);
  const supplierRows = useMemo(() => {
    const terms = sq.trim().split(/\s+/).filter(Boolean);
    return suppliers.filter((s) => {
      if (sChip === "active" && !s.isActive) return false;
      if (sChip === "passive" && s.isActive) return false;
      const hay = [s.name, s.code || "", s.contactPerson || "", s.taxNumber || ""];
      return terms.every((t) => hay.some((h) => turkishIncludes(h, t)));
    }).sort((a, b) => a.name.localeCompare(b.name, "tr"));
  }, [suppliers, sq, sChip]);
  const filterOn = !!q || chip !== "open" || lateOnly || !!supplierFilter;
  const resetFilters = () => { setQ(""); setChip("open"); setLateOnly(false); setSupplierFilter(""); };

  // ---- İşlemler (tek yerde: onay, bildirim, yenileme) ----
  const after = async (msg?: string) => { await load(); setRefreshKey((k) => k + 1); if (msg) ok(msg); };
  const fetchDetail = async (id: string) => {
    setRowBusy(id);
    try { return await purchasingService.order(id); }
    catch (e) { fail(poErr(e, "Sipariş yüklenemedi.")); return null; }
    finally { setRowBusy(null); }
  };
  const editOrder = async (x: string | PurchaseOrderDetail) => {
    const d = typeof x === "string" ? await fetchDetail(x) : x;
    if (!d) return;
    if (d.status !== S.Draft) return warn("Yalnız taslak siparişler düzenlenebilir.");
    setModal({ open: true, order: d });
  };
  const receiveOrder = async (x: string | PurchaseOrderDetail) => {
    const d = typeof x === "string" ? await fetchDetail(x) : x;
    if (d) setReceiveFor(d);
  };
  const sendOrder = (o: PurchaseOrderListItem) => setConfirm({
    isOpen: true, type: "info", title: "Tedarikçiye ilet", confirmText: "İlet",
    message: `${o.number} tedarikçiye iletildi olarak işaretlenecek. İletilen sipariş düzenlenemez ve silinemez; yalnız mal kabul ya da iptal yapılabilir.`,
    onConfirm: async () => {
      closeConfirm(); setBusy(true);
      try { await purchasingService.markOrdered(o.purchaseOrderId); await after(`${o.number} tedarikçiye iletildi.`); }
      catch (e) { fail(poErr(e, "İşlem yapılamadı.")); }
      finally { setBusy(false); }
    },
  });
  const deleteOrder = (o: PurchaseOrderListItem) => setConfirm({
    isOpen: true, type: "danger", title: "Taslağı sil", confirmText: "Sil",
    message: `${o.number} taslak siparişi kalıcı olarak silinecek.`,
    onConfirm: async () => {
      closeConfirm(); setBusy(true);
      try {
        await purchasingService.deleteOrder(o.purchaseOrderId);
        if (drawerId === o.purchaseOrderId) closeDrawer();
        await after("Taslak silindi.");
      } catch (e) { fail(poErr(e, "Silinemedi.")); }
      finally { setBusy(false); }
    },
  });
  const cancelOrder = async (reason: string) => {
    if (!cancelFor) return;
    try { await purchasingService.cancel(cancelFor.purchaseOrderId, reason); const n = cancelFor.number; setCancelFor(null); await after(`${n} iptal edildi.`); }
    catch (e) { setCancelErr(poErr(e, "Sipariş iptal edilemedi.")); }
  };
  const deleteSupplier = (s: Supplier) => setConfirm({
    isOpen: true, type: "danger", title: "Tedarikçiyi sil", confirmText: "Sil",
    message: `${s.name} silinsin mi? Siparişi, stok bağı veya cari kaydı olan tedarikçi silinmez, pasife alınır.`,
    onConfirm: async () => {
      closeConfirm(); setBusy(true);
      try {
        const r = await purchasingService.deleteSupplier(s.supplierId); await load();
        // Sunucu mesajı her engelde "siparişi olan" der; gerçek sebep (sipariş, stok bağı, cari) ayrılmaz
        ok(r?.deleted === false ? `${s.name} bağlı kayıtları olduğu için silinmedi, pasife alındı.` : "Tedarikçi silindi.");
      }
      catch (e) { fail(poErr(e, "Tedarikçi silinemedi.")); }
      finally { setBusy(false); }
    },
  });
  const onModalSaved = async (r: { purchaseOrderId: string; number?: string; ordered: boolean; orderError?: string }) => {
    setModal({ open: false, order: null });
    await load(); setRefreshKey((k) => k + 1);
    if (drawerId !== r.purchaseOrderId) navigate(`/purchasing/${r.purchaseOrderId}`, { state: { fromList: true } });
    if (r.orderError) warn(`Sipariş taslak olarak kaydedildi ancak iletilemedi: ${r.orderError}`);
    else ok(r.ordered ? `${r.number ?? "Sipariş"} kaydedildi ve tedarikçiye iletildi.` : `${r.number ?? "Sipariş"} taslak olarak kaydedildi.`);
  };

  if (initial) return <PageLoader message="Satın alma yükleniyor..." />;

  const open = moneyMain(stats?.openAmountByCurrency);
  const recv = moneyMain(stats?.receivedLast30DaysByCurrency);

  return (
    <div className="po-page">
      <header className="po-head">
        <div>
          <span className="po-kicker">Tedarik</span>
          <h1>Satın Alma</h1>
          <p>Tedarikçiye verilen siparişler, beklenen teslimatlar ve mal kabul. {hasModule("stock") ? "Teslim alınan miktar stoğa giriş olarak işlenir." : "Stok modülü kapalı; yalnız teslim miktarı izlenir."}</p>
        </div>
        <div className="po-head-actions">
          <button type="button" className="po-btn" onClick={refresh} disabled={busy}><span className={busy ? "po-spin-icon" : ""} style={{ display: "inline-flex" }}>{I.refresh}</span> Yenile</button>
          {tab === "orders" && has("purchasing:orders:create") && <button type="button" className="po-btn primary" onClick={() => setModal({ open: true, order: null })}>{I.plus} Yeni sipariş</button>}
          {tab === "suppliers" && has("purchasing:suppliers:create") && <button type="button" className="po-btn primary" onClick={() => setSupplierModal({ open: true })}>{I.plus} Yeni tedarikçi</button>}
        </div>
      </header>

      {loadError && <div className="po-note err" role="alert">{loadError} <button type="button" className="po-link" onClick={refresh}>Yeniden dene</button></div>}

      {stats && (
        <section className="po-kpis">
          <div className="po-kpi navy"><span className="l">Teslim bekleyen</span><span className="v">{stats.openCount}<small> sipariş</small></span><span className="s"><b>{stats.draftCount}</b> taslak ayrıca</span></div>
          <button type="button" className={`po-kpi ${stats.overdueCount > 0 ? "err" : "ok"} ${lateOnly ? "on" : ""}`} aria-pressed={lateOnly} onClick={() => { setTab("orders"); setLateOnly((v) => !v); }}>
            <span className="l">Teslimi geciken</span><span className="v">{stats.overdueCount}</span><span className="s">{stats.overdueCount > 0 ? "beklenen tarihi geçmiş · süzmek için tıklayın" : "gecikme yok"}</span>
          </button>
          <div className="po-kpi"><span className="l">Bekleyen tutar</span><span className="v">{open.main ? formatCurrency(open.main[1], open.main[0]) : "—"}</span><span className="s">teslim alınmamış kalan · KDV hariç</span>{open.rest && <span className="s" title={open.rest}>+ {open.rest}</span>}</div>
          <div className="po-kpi ok"><span className="l">Son 30 gün alım</span><span className="v">{recv.main ? formatCurrency(recv.main[1], recv.main[0]) : "—"}</span><span className="s">mal kabul edilen · KDV hariç</span>{recv.rest && <span className="s" title={recv.rest}>+ {recv.rest}</span>}</div>
        </section>
      )}

      {tab === "orders" && attention.length > 0 && (
        <div className="po-section">
          <h2>Geciken ve yaklaşan teslimatlar</h2>
          <div className="po-upcoming">
            {attention.map(({ o, d }) => (
              <button type="button" key={o.purchaseOrderId} className={`po-up ${d.state}`} onClick={() => openDrawer(o.purchaseOrderId)}>
                <span className="po-mark" style={{ ["--po-hue" as any]: firmHue(o.supplierName) }}>{firmInitials(o.supplierName)}</span>
                <div><b>{o.supplierName}</b><small>{d.text} · {o.number}</small></div>
                <span className="amt">{formatCurrency(o.grossTotal, o.currency)}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="po-tabs" role="tablist">
        {canOrders && <button type="button" role="tab" aria-selected={tab === "orders"} className={tab === "orders" ? "on" : ""} onClick={() => setTab("orders")}>Siparişler <span>{orders.length}</span></button>}
        {canSuppliers && <button type="button" role="tab" aria-selected={tab === "suppliers"} className={tab === "suppliers" ? "on" : ""} onClick={() => setTab("suppliers")}>Tedarikçiler <span>{suppliers.filter((s) => s.isActive).length}</span></button>}
      </div>

      {tab === "orders" ? (
        <section className={`po-card ${busy ? "dim" : ""}`}>
          <div className="po-toolbar">
            <div className="po-search">{I.search}<input placeholder="Sipariş no, tedarikçi veya tutar ara" value={q} onChange={(e) => setQ(e.target.value)} />{q && <button type="button" className="po-ib" aria-label="Temizle" onClick={() => setQ("")}>{I.close}</button>}</div>
            <div className="po-chipgroup" role="group" aria-label="Durum">
              {CHIPS.map(([k, l]) => <button type="button" key={k} className={chip === k ? "on" : ""} aria-pressed={chip === k} onClick={() => setChip(k)}>{l}<i>{counts[k]}</i></button>)}
            </div>
            {(lateCount > 0 || lateOnly) && <button type="button" className={`po-chip danger ${lateOnly ? "on" : ""}`} aria-pressed={lateOnly} onClick={() => setLateOnly((v) => !v)}>{I.warn} Geciken <i>{lateCount}</i></button>}
            <SupplierPickerField variant="toolbar" value={supplierFilter} suppliers={suppliers} nullLabel="Tüm tedarikçiler" includeInactive title="Tedarikçiye göre süz" onChange={(s) => setSupplierFilter(s?.supplierId || "")} />
            {filterOn && <button type="button" className="po-clear" onClick={resetFilters}>{I.filterOff} Temizle</button>}
          </div>
          <div className="po-card-head">
            <span><b>{rows.length}</b> sipariş</span>
            <span className="po-hint">{listTotals.map(([c, v]) => <b key={c} style={{ marginLeft: "0.75rem" }}>{formatCurrency(v, c)}</b>)}{listTotals.length > 0 && " · KDV dahil"}</span>
          </div>
          {rows.length === 0 ? (
            !loaded ? (
              <div className="po-blank"><span className="po-blank-icon">{I.warn}</span><b>Siparişler yüklenemedi</b>Bağlantıyı kontrol edip yeniden deneyin.
                <button type="button" className="po-btn" onClick={refresh} disabled={busy}>{I.refresh} Yeniden dene</button></div>
            ) : orders.length === 0 ? (
              <div className="po-blank"><span className="po-blank-icon">{I.inbox}</span><b>Henüz satın alma siparişi yok</b>İlk siparişi oluşturun; tedarikçiye iletildiğinde teslimat buradan izlenir.
                {has("purchasing:orders:create") && <button type="button" className="po-btn primary" onClick={() => setModal({ open: true, order: null })}>{I.plus} İlk siparişi oluştur</button>}</div>
            ) : (
              <div className="po-blank"><span className="po-blank-icon">{I.inbox}</span><b>Süzgeçle eşleşen sipariş yok</b>Süzgeci değiştirin.
                <button type="button" className="po-btn" onClick={resetFilters}>Süzgeci sıfırla</button></div>
            )
          ) : (
            <div className="po-table-wrap">
              <table className="po-table">
                <thead><tr><th>Sipariş</th><th className="po-hide-sm">Tedarikçi</th><th>Teslim</th><th className="po-hide-md">Teslimat</th><th>Durum</th><th className="num">Tutar</th><th className="act" /></tr></thead>
                <tbody>
                  {rows.map((o) => {
                    const id = o.purchaseOrderId;
                    const due = poDue(o.expectedDate, o.status);
                    const waiting = poIsWaiting(o.status);
                    const pct = o.orderedQuantity > 0 ? Math.min(100, (o.receivedQuantity / o.orderedQuantity) * 100) : 0;
                    return (
                      <tr key={id} className={[drawerId === id ? "on" : "", due.state === "late" ? "late" : "", o.status === S.Cancelled ? "off" : ""].join(" ")} onClick={() => openDrawer(id)}>
                        <td><div className="po-row">
                          <span className={`po-mark ${o.status === S.Cancelled ? "off" : ""}`} style={{ ["--po-hue" as any]: firmHue(o.supplierName) }}>{firmInitials(o.supplierName)}</span>
                          <div><div className="po-code">{o.number}</div><div className="po-sub">{o.lineCount} kalem · {poFmtDate(o.orderDate)}</div><div className="po-sub po-only-sm">{o.supplierName}</div></div>
                        </div></td>
                        <td className="po-hide-sm"><div className="po-name">{o.supplierName}</div><div className="po-sub po-hide-md">oluşturan {o.createdBy || "—"}</div></td>
                        <td className={`po-due ${due.state}`}>
                          {waiting ? (o.expectedDate ? <>{poFmtDate(o.expectedDate)}<div className="po-sub">{due.text}</div></> : <span className="po-sub"><i>tarih yok</i></span>)
                            : o.status === S.Received ? <span className="po-sub">teslim alındı</span>
                              : o.status === S.Draft && o.expectedDate ? <span className="po-sub">{poFmtDate(o.expectedDate)}</span> : "—"}
                        </td>
                        <td className="po-hide-md">
                          {o.status === S.Draft || o.status === S.Cancelled ? "—" : (
                            <div className={`po-progress ${pct >= 100 ? "done" : due.state === "late" ? "late" : ""}`}>
                              <div className="po-progress-bar"><i style={{ width: `${pct}%` }} /></div>
                              <span className="po-progress-text">{poFmtQty(o.receivedQuantity)} / {poFmtQty(o.orderedQuantity)}</span>
                            </div>
                          )}
                        </td>
                        <td><span className={`po-st s${o.status}`}>{PO_STATUS_LABEL[o.status]}</span></td>
                        <td className="num"><span className="po-amt">{formatCurrency(o.grossTotal, o.currency)}</span></td>
                        <td className="act"><div className="po-rowacts" onClick={(e) => e.stopPropagation()}>
                          {o.status === S.Draft && canUpdate && <>
                            <button type="button" className="po-ib" title="Düzenle" aria-label={`${o.number} düzenle`} disabled={rowBusy === id} onClick={() => editOrder(id)}>{I.edit}</button>
                            <button type="button" className="po-ib" title="Tedarikçiye ilet" aria-label={`${o.number} tedarikçiye ilet`} onClick={() => sendOrder(o)}>{I.send}</button>
                          </>}
                          {waiting && canUpdate && <button type="button" className="po-btn small" disabled={rowBusy === id} onClick={() => receiveOrder(id)}>{I.receive} Mal kabul</button>}
                          <button type="button" className="po-ib" title="Sipariş kartı" aria-label={`${o.number} sipariş kartı`} onClick={() => openDrawer(id)}>{I.eye}</button>
                        </div></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : (
        <section className={`po-card ${busy ? "dim" : ""}`}>
          <div className="po-toolbar">
            <div className="po-search">{I.search}<input placeholder="Tedarikçi adı, kod, yetkili veya vergi no ara" value={sq} onChange={(e) => setSq(e.target.value)} />{sq && <button type="button" className="po-ib" aria-label="Temizle" onClick={() => setSq("")}>{I.close}</button>}</div>
            <div className="po-chipgroup" role="group" aria-label="Durum">
              {([["active", "Aktif"], ["passive", "Pasif"], ["all", "Hepsi"]] as const).map(([k, l]) => <button type="button" key={k} className={sChip === k ? "on" : ""} aria-pressed={sChip === k} onClick={() => setSChip(k)}>{l}</button>)}
            </div>
            {(sq || sChip !== "active") && <button type="button" className="po-clear" onClick={() => { setSq(""); setSChip("active"); }}>{I.filterOff} Temizle</button>}
          </div>
          <div className="po-card-head">
            <span><b>{supplierRows.length}</b> tedarikçi</span>
            {hasModule("stock") && hasScreen("suppliers") && <button type="button" className="po-link" onClick={() => navigate("/suppliers")}>Tedarikçi kartları</button>}
          </div>
          {supplierRows.length === 0 ? (
            !loaded ? (
              <div className="po-blank"><span className="po-blank-icon">{I.warn}</span><b>Tedarikçiler yüklenemedi</b>Bağlantıyı kontrol edip yeniden deneyin.
                <button type="button" className="po-btn" onClick={refresh} disabled={busy}>{I.refresh} Yeniden dene</button></div>
            ) : suppliers.length === 0 ? (
              <div className="po-blank"><span className="po-blank-icon">{I.inbox}</span><b>Henüz tedarikçi yok</b>Sipariş verebilmek için önce tedarikçi kartı açın.
                {has("purchasing:suppliers:create") && <button type="button" className="po-btn primary" onClick={() => setSupplierModal({ open: true })}>{I.plus} İlk tedarikçiyi oluştur</button>}</div>
            ) : (
              <div className="po-blank"><span className="po-blank-icon">{I.inbox}</span><b>Aramayla eşleşen tedarikçi yok</b>Süzgeci değiştirin.</div>
            )
          ) : (
            <div className="po-table-wrap">
              <table className="po-table">
                <thead><tr><th>Tedarikçi</th><th className="po-hide-md">Yetkili</th><th className="po-hide-sm">İletişim</th><th className="po-hide-lg">VKN</th><th>Vade</th><th className="num">Açık sipariş</th><th>Durum</th><th className="act" /></tr></thead>
                <tbody>
                  {supplierRows.map((s) => {
                    const ob = openBySupplier.get(s.supplierId);
                    return (
                      <tr key={s.supplierId} className={`static ${s.isActive ? "" : "off"}`}>
                        <td><div className="po-row">
                          <span className={`po-mark ${s.isActive ? "" : "off"}`} style={{ ["--po-hue" as any]: firmHue(s.code || s.name) }}>{firmInitials(s.name)}</span>
                          <div><div className="po-name">{s.name}</div><div className="po-sub">{s.code || "kod yok"}</div></div>
                        </div></td>
                        <td className="po-hide-md">{s.contactPerson || "—"}</td>
                        <td className="po-hide-sm">{s.phone || "—"}{s.email && <div className="po-sub">{s.email}</div>}</td>
                        <td className="po-hide-lg">{s.taxNumber || "—"}</td>
                        <td>{s.paymentTermDays} gün<div className="po-sub">{s.currency}</div></td>
                        <td className="num">{ob ? ob.count : "—"}{ob && ob.late > 0 && <div className="po-sub danger">{ob.late} geciken</div>}</td>
                        <td><span className={`po-badge ${s.isActive ? "ok" : "muted"}`}>{s.isActive ? "Aktif" : "Pasif"}</span></td>
                        <td className="act"><div className="po-rowacts">
                          {has("purchasing:orders:create") && s.isActive && <button type="button" className="po-ib" title="Sipariş oluştur" aria-label={`${s.name} için sipariş oluştur`} onClick={() => setModal({ open: true, order: null, supplierId: s.supplierId })}>{I.cart}</button>}
                          <button type="button" className="po-ib" title="Siparişlerini göster" aria-label={`${s.name} siparişlerini göster`} onClick={() => { setTab("orders"); setSupplierFilter(s.supplierId); setChip("all"); setLateOnly(false); setQ(""); }}>{I.arrow}</button>
                          {has("purchasing:suppliers:update") && <button type="button" className="po-ib" title="Düzenle" aria-label={`${s.name} düzenle`} onClick={() => setSupplierModal({ open: true, supplier: s })}>{I.edit}</button>}
                          {has("purchasing:suppliers:delete") && <button type="button" className="po-ib danger" title="Sil" aria-label={`${s.name} sil`} onClick={() => deleteSupplier(s)}>{I.trash}</button>}
                        </div></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* Katmanlar */}
      {drawerId && (
        <PurchaseOrderDrawer orderId={drawerId} preview={orders.find((o) => o.purchaseOrderId === drawerId)} refreshKey={refreshKey} busy={busy}
          escDisabled={confirm.isOpen || modal.open || supplierModal.open || !!receiveFor || !!cancelFor}
          onClose={closeDrawer} onEdit={editOrder} onSend={sendOrder} onReceive={receiveOrder}
          onCancel={(o) => { setCancelErr(""); setCancelFor(o); }} onDelete={deleteOrder} />
      )}
      {modal.open && (
        <PurchaseOrderModal order={modal.order} initialSupplierId={modal.supplierId} suppliers={suppliers} onSuppliersChanged={reloadSuppliers} onClose={() => setModal({ open: false, order: null })} onSaved={onModalSaved} />
      )}
      {receiveFor && <ReceiveModal order={receiveFor} onClose={() => setReceiveFor(null)} onSaved={() => after(`Mal kabul kaydedildi · ${receiveFor.number}`)} />}
      {cancelFor && <PurchaseCancelModal order={cancelFor} error={cancelErr} onClose={() => setCancelFor(null)} onConfirm={cancelOrder} />}
      {supplierModal.open && <SupplierModal supplier={supplierModal.supplier} onClose={() => setSupplierModal({ open: false })} onSaved={() => load()} />}
      <AlertModal isOpen={alert.isOpen} message={alert.message} type={alert.type} onClose={() => setAlert((a) => ({ ...a, isOpen: false }))} />
      <ConfirmModal isOpen={confirm.isOpen} title={confirm.title} message={confirm.message} type={confirm.type} confirmText={confirm.confirmText} cancelText="Vazgeç" onConfirm={confirm.onConfirm} onCancel={closeConfirm} />
    </div>
  );
};

export default Purchasing;
